/**
 * Tenant → Configuración → Legal y verificación
 *
 * Muestra documentos publicados aplicables, requisitos de aceptación pendientes,
 * historial de aceptaciones y estado KYC.
 *
 * La fuente de verdad es GET /api/legal/accept que usa resolveLegalAcceptanceRequirements.
 *
 * Todo documento se lee en un modal dentro del panel del tenant — nunca se
 * navega a la página pública (esa existe solo para visitantes sin sesión).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, FileText, Eye } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ALL_LEGAL_DOCUMENT_TYPES, LEGAL_DOCUMENT_TITLES } from "@/lib/legal-public-links";
import { LegalMarkdown } from "@/components/legal/LegalMarkdown";
import { KycVerificationCard } from "@/components/settings/KycVerificationCard";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";

export const Route = createFileRoute("/app/settings/legal")({
  component: TenantLegalPage,
  head: () => ({ meta: [{ title: "Legal y verificación" }] }),
});

interface LegalRequirement {
  documentId: string;
  documentType: string;
  documentTitle: string;
  versionId: string;
  versionCode: string;
  required: boolean;
  accepted: boolean;
  acceptedAt: string | null;
}

interface LegalHistory {
  id: string;
  documentId: string;
  documentVersionId: string;
  documentType: string;
  documentTitle: string;
  versionCode: string;
  acceptedAt: string;
  context: string | null;
}

const CONTEXT_LABELS: Record<string, string> = {
  onboarding: "Onboarding",
  reacceptance: "Re-aceptación",
  settings: "Configuración",
  api: "API",
};

/** Qué documento está abierto en el modal — con datos de aceptación si aplica (checkbox+botón), sin ellos si es solo lectura. */
type ViewerState =
  | { type: string; title: string; requirement: LegalRequirement }
  | { type: string; title: string; requirement: null };

function DocumentDialog({
  state,
  onAccepted,
  onClose,
}: {
  state: ViewerState;
  onAccepted: () => void;
  onClose: () => void;
}) {
  const { type, title, requirement } = state;
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [content, setContent] = useState<string | null>(null);
  const [contentError, setContentError] = useState(false);
  const [isDraftPreview, setIsDraftPreview] = useState(false);
  const tenantId = useWorkspaceTenantId();

  useEffect(() => {
    setContent(null);
    setContentError(false);
    setConfirmed(false);
    fetch(`/api/legal/public?type=${encodeURIComponent(type)}`)
      .then((r) => r.json())
      .then((d: { version?: { content?: string }; isDraftPreview?: boolean }) => {
        if (d.version?.content) {
          setContent(d.version.content);
          setIsDraftPreview(d.isDraftPreview === true);
        } else {
          setContentError(true);
        }
      })
      .catch(() => setContentError(true));
  }, [type]);

  const handleAccept = async () => {
    if (!requirement || !confirmed) return;
    setSubmitting(true);
    try {
      // Enviamos el versionId del documento que el usuario está viendo,
      // no "el publicado actual" — garantiza coherencia con lo renderizado.
      await apiSendJson(
        "/api/legal/accept",
        "POST",
        {
          documentVersionId: requirement.versionId,
          context: "settings",
          acceptanceMethod: "click",
        },
        tenantId,
      );
      toast.success("Aceptación registrada correctamente");
      onAccepted();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error registrando aceptación");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl border border-border bg-background shadow-xl">
        <div className="shrink-0 border-b border-border p-6 pb-4">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-primary" />
            <h2 className="text-base font-semibold text-foreground">{title}</h2>
          </div>
          {requirement && (
            <p className="mt-1 text-sm text-muted-foreground">
              Versión <span className="font-mono font-medium text-foreground">{requirement.versionCode}</span>
            </p>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {isDraftPreview && (
            <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
              Borrador — todavía no fue publicado ni revisado legalmente.
            </div>
          )}
          {content === null && !contentError && (
            <p className="text-sm text-muted-foreground">Cargando documento…</p>
          )}
          {contentError && (
            <p className="text-sm text-muted-foreground">
              Este documento todavía no está disponible.
            </p>
          )}
          {content && (
            <div className="prose prose-sm max-w-none text-foreground">
              <LegalMarkdown content={content} />
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-border p-6 pt-4">
          {requirement ? (
            <>
              <div className="mb-4 rounded-lg border border-border bg-muted/30 p-4">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-primary"
                  />
                  <span className="text-sm text-foreground leading-snug">
                    Declaro que leí y acepto <strong>{requirement.documentTitle}</strong>, versión{" "}
                    <span className="font-mono">{requirement.versionCode}</span>.
                  </span>
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
                  Cancelar
                </Button>
                <Button size="sm" disabled={!confirmed || submitting} onClick={handleAccept}>
                  {submitting ? "Registrando…" : "Aceptar y continuar"}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={onClose}>
                Cerrar
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TenantLegalPage() {
  const tenantId = useWorkspaceTenantId();
  const [requirements, setRequirements] = useState<LegalRequirement[]>([]);
  const [history, setHistory] = useState<LegalHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState<ViewerState | null>(null);

  const reload = () => {
    setLoading(true);
    apiGetJson<{ requirements?: LegalRequirement[]; history?: LegalHistory[] }>(
      "/api/legal/accept",
      tenantId,
    )
      .then((d) => {
        setRequirements(d.requirements ?? []);
        setHistory(d.history ?? []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(reload, [tenantId]);

  const pending = requirements.filter((r) => !r.accepted);
  const accepted = requirements.filter((r) => r.accepted);
  const requirementByType = new Map(requirements.map((r) => [r.documentType, r]));

  return (
    <div className="space-y-6 p-6">
      {viewer && (
        <DocumentDialog
          state={viewer}
          onAccepted={() => { setViewer(null); reload(); }}
          onClose={() => setViewer(null)}
        />
      )}

      <div>
        <h1 className="text-xl font-bold text-foreground">Legal y verificación</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Documentos legales aplicables a tu cuenta y estado de verificación comercial.
        </p>
      </div>

      {/* Documentos pendientes de aceptación */}
      {!loading && pending.length > 0 && (
        <Card className="border-amber-300 dark:border-amber-700">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4" />
              Documentos pendientes de aceptación ({pending.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {pending.map((req) => (
              <div
                key={req.documentId}
                className="flex items-center justify-between border-t border-border px-4 py-3"
              >
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="font-medium text-foreground">{req.documentTitle}</p>
                  <p className="text-xs text-muted-foreground">
                    Versión {req.versionCode}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() =>
                    setViewer({ type: req.documentType, title: req.documentTitle, requirement: req })
                  }
                >
                  Revisar y aceptar
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Documentos aceptados */}
      {!loading && accepted.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-green-700 dark:text-green-400">
              <CheckCircle2 className="h-4 w-4" />
              Documentos aceptados
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {accepted.map((req) => (
              <div key={req.documentId} className="flex items-center justify-between border-t border-border px-4 py-3">
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="font-medium text-foreground">{req.documentTitle}</p>
                  <p className="text-xs text-muted-foreground">
                    v{req.versionCode}
                    {req.acceptedAt && ` · aceptado el ${new Date(req.acceptedAt).toLocaleDateString("es-AR")}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                    Aceptado
                  </Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2"
                    onClick={() =>
                      setViewer({ type: req.documentType, title: req.documentTitle, requirement: null })
                    }
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Documentos legales — los 10 tipos canónicos, lectura dentro del panel */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Documentos legales</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {ALL_LEGAL_DOCUMENT_TYPES.map((type) => {
            const req = requirementByType.get(type);
            return (
              <button
                key={type}
                type="button"
                onClick={() =>
                  setViewer({
                    type,
                    title: LEGAL_DOCUMENT_TITLES[type],
                    requirement: req && !req.accepted ? req : null,
                  })
                }
                className="flex w-full items-center justify-between border-t border-border px-4 py-3 text-left transition-colors hover:bg-muted/30"
              >
                <span className="text-sm text-foreground">{LEGAL_DOCUMENT_TITLES[type]}</span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  Ver <Eye className="h-3 w-3" />
                </span>
              </button>
            );
          })}
        </CardContent>
      </Card>

      {/* Historial de aceptaciones */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Historial de aceptaciones</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && (
            <p className="px-4 py-3 text-sm text-muted-foreground">Cargando…</p>
          )}
          {!loading && history.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              Sin aceptaciones registradas.
            </p>
          )}
          {!loading && history.map((a) => (
            <div
              key={a.id}
              className="flex items-center justify-between border-t border-border px-4 py-2.5"
            >
              <div className="flex min-w-0 items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-500" />
                <span className="truncate text-sm text-foreground">
                  {a.documentTitle}{" "}
                  <span className="font-mono text-xs text-muted-foreground">v{a.versionCode}</span>
                </span>
                {a.context && (
                  <Badge variant="secondary" className="shrink-0 text-xs">
                    {CONTEXT_LABELS[a.context] ?? a.context}
                  </Badge>
                )}
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">
                {new Date(a.acceptedAt).toLocaleString("es-AR", {
                  dateStyle: "short",
                  timeStyle: "short",
                })}
              </span>
            </div>
          ))}
        </CardContent>
      </Card>

      <KycVerificationCard />
    </div>
  );
}
